import { useState, useEffect, useRef } from 'react';
import { api, fmt } from '../api';
import toast from 'react-hot-toast';
import NavIcon from '../components/NavIcon';
import { confirmDialog } from '../components/ConfirmDialog';

function ItemRow({ item, products, onChange, onRemove }) {
  const [qty, setQty] = useState(String(item.quantity));

  useEffect(() => { setQty(String(item.quantity)); }, [item.quantity]);

  const commitQty = () => {
    const n = Number(qty);
    if (!isNaN(n) && n > 0 && n !== item.quantity) onChange(item.id, { productId: item.productId, quantity: n });
    else setQty(String(item.quantity));
  };

  const changeProduct = (productId) => {
    onChange(item.id, { productId: productId ? Number(productId) : null, quantity: item.quantity });
  };

  return (
    <div className="table-order-item-row">
      <div>
        {item.isResolved ? (
          <select value={item.productId || ''} onChange={e => changeProduct(e.target.value)}>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        ) : (
          <select value="" onChange={e => changeProduct(e.target.value)} style={{ borderColor: 'var(--danger)' }}>
            <option value="">⚠ "{item.shortcutText}" not recognized — pick a product</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>
      <input value={qty} onChange={e => setQty(e.target.value)} onBlur={commitQty} />
      <span className="mono text-muted">{fmt.currency(item.unitPrice)}</span>
      <span className="mono" style={{ fontWeight: 700 }}>{fmt.currency(item.quantity * item.unitPrice)}</span>
      <button onClick={() => onRemove(item.id)} title="Remove line" style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 15 }}>✕</button>
    </div>
  );
}

function NoteCard({ note, products, onChanged }) {
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const items = note.items || [];
  const total = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
  const allResolved = items.length > 0 && items.every(i => i.isResolved);

  const handleItemChange = async (itemId, { productId, quantity }) => {
    try {
      await api.updateQuickNoteItem(itemId, { productId, quantity });
      onChanged();
    } catch (err) {
      toast.error(err?.message || 'Failed to update line');
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await api.deleteQuickNoteItem(itemId);
      onChanged();
    } catch { toast.error('Failed to remove line'); }
  };

  const approve = async () => {
    setApproving(true);
    try {
      await api.approveQuickNote(note.id);
      toast.success('Approved — added to Sales');
      onChanged();
    } catch (err) {
      toast.error(err?.message || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const reject = async () => {
    if (!(await confirmDialog('Discard this note without creating a sale?', { danger: true, confirmText: 'Discard' }))) return;
    setRejecting(true);
    try {
      await api.rejectQuickNote(note.id);
      toast.success('Note discarded');
      onChanged();
    } catch (err) {
      toast.error(err?.message || 'Failed to discard');
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div className="card mb-6">
      <div className="flex-between" style={{ marginBottom: 10 }}>
        <span className="text-muted" style={{ fontSize: 12 }}>{fmt.datetime(note.createdAt)}</span>
        <span className="text-muted" style={{ fontSize: 12, fontStyle: 'italic' }}>"{note.rawText.split('\n')[0]}{note.rawText.includes('\n') ? '…' : ''}"</span>
      </div>

      {items.length === 0 ? (
        <p className="text-muted" style={{ fontSize: 13 }}>No recognized lines — nothing to approve here. You can only discard it.</p>
      ) : (
        <>
          <div className="table-order-item-row-header">
            <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>Product</div>
            <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>Qty</div>
            <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>Price</div>
            <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>Total</div>
            <div />
          </div>
          {items.map(item => (
            <ItemRow key={item.id} item={item} products={products} onChange={handleItemChange} onRemove={handleRemove} />
          ))}
          <div className="flex-between" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <span style={{ fontWeight: 700 }}>Total</span>
            <span className="mono" style={{ fontWeight: 700, fontSize: 16 }}>{fmt.currency(total)}</span>
          </div>
        </>
      )}

      <div className="modal-footer" style={{ marginTop: 14 }}>
        <button className="btn btn-secondary" onClick={reject} disabled={rejecting}>
          {rejecting ? 'Discarding…' : 'Discard'}
        </button>
        <button className="btn btn-success" onClick={approve} disabled={approving || !allResolved || items.length === 0}>
          {approving ? 'Approving…' : 'Approve → Sale'}
        </button>
      </div>
      {!allResolved && items.length > 0 && (
        <p className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>Resolve every unrecognized line above before approving.</p>
      )}
    </div>
  );
}

export default function QuickNotes() {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState([]);
  const [products, setProducts] = useState([]);
  const [shortcuts, setShortcuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refSearch, setRefSearch] = useState('');

  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [highlighted, setHighlighted] = useState(0);
  const [tokenStart, setTokenStart] = useState(null); // index of the "." that starts the token being typed
  const textareaRef = useRef(null);

  const load = async () => {
    try {
      const [notes, prod, sc] = await Promise.all([
        api.getPendingQuickNotes(), api.getProductsLookup(), api.getShortcuts(),
      ]);
      setPending(notes);
      setProducts(prod);
      setShortcuts(sc.filter(s => s.isActive));
    } catch {
      toast.error('Failed to load quick notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const note = await api.createQuickNote(text);
      const recognized = note.items?.length || 0;
      if (recognized === 0) {
        toast('No lines recognized — check the format, e.g. "5 .c5"', { icon: '⚠️' });
      } else {
        toast.success(`Saved — ${recognized} line${recognized !== 1 ? 's' : ''} recognized`);
      }
      setText('');
      setSuggestions([]);
      load();
    } catch (err) {
      toast.error(err?.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  // Look at the text right up to the cursor — if it ends in ".partial",
  // treat that as an in-progress shortcut and suggest matches.
  const updateSuggestions = (value, cursorPos) => {
    const before = value.slice(0, cursorPos);
    const match = before.match(/\.(\w*)$/);
    if (!match) {
      setSuggestions([]);
      setTokenStart(null);
      return;
    }
    const partial = match[1].toLowerCase();
    const dotIndex = cursorPos - match[0].length;
    const matches = shortcuts
      .filter(s => s.shortcut.toLowerCase().startsWith(partial))
      .slice(0, 6);
    setSuggestions(matches);
    setTokenStart(matches.length > 0 ? dotIndex : null);
    setHighlighted(0);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setText(value);
    updateSuggestions(value, e.target.selectionStart);
  };

  const handleSelectionChange = () => {
    if (!textareaRef.current) return;
    updateSuggestions(text, textareaRef.current.selectionStart);
  };

  const applySuggestion = (shortcut) => {
    if (tokenStart === null) return;
    const cursorPos = textareaRef.current?.selectionStart ?? text.length;
    const newText = text.slice(0, tokenStart) + '.' + shortcut.shortcut + ' ' + text.slice(cursorPos);
    setText(newText);
    setSuggestions([]);
    setTokenStart(null);
    const newCursor = tokenStart + shortcut.shortcut.length + 2;
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newCursor, newCursor);
    });
  };

  const handleKeyDown = (e) => {
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      applySuggestion(suggestions[highlighted]);
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setTokenStart(null);
    }
  };

  const filteredRef = shortcuts.filter(s => {
    const q = refSearch.trim().toLowerCase();
    if (!q) return true;
    return s.shortcut.toLowerCase().includes(q) || s.productName?.toLowerCase().includes(q);
  });

  return (
    <div className="page">
      <div className="page-header">
        <h2>Quick Notes</h2>
        <p className="page-sub">Jot down sales shorthand throughout the day, then review and approve into real sales when you have a moment</p>
      </div>

      <div className="calendar-layout mb-6">
        <div className="card" style={{ position: 'relative' }}>
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <NavIcon name="document" size={14} /> Quick Entry
          </div>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 10 }}>
            One line per item: a quantity, then a dot and the product's shortcut — e.g. <code className="mono">5 .c5</code> for
            5× whatever product has the shortcut "c5". Start typing after the dot for suggestions.
          </p>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleChange}
            onClick={handleSelectionChange}
            onKeyUp={handleSelectionChange}
            onKeyDown={handleKeyDown}
            onBlur={() => setTimeout(() => setSuggestions([]), 150)}
            placeholder={"5 .c5\n2 .esp\n1 .mocha"}
            rows={5}
            style={{
              width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontFamily: 'var(--mono)',
              fontSize: 14, resize: 'vertical',
            }}
          />

          {suggestions.length > 0 && (
            <div style={{
              position: 'absolute', zIndex: 50, background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 7, boxShadow: 'var(--shadow, 0 8px 28px rgba(0,0,0,0.35))',
              marginTop: -6, width: 'min(260px, 100%)', overflow: 'hidden',
            }}>
              {suggestions.map((s, i) => (
                <div
                  key={s.id}
                  onMouseDown={(e) => { e.preventDefault(); applySuggestion(s); }}
                  onMouseEnter={() => setHighlighted(i)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', gap: 10,
                    padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                    background: i === highlighted ? 'var(--hover)' : 'transparent',
                  }}
                >
                  <span className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>.{s.shortcut}</span>
                  <span className="text-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.productName}</span>
                </div>
              ))}
              <div style={{ padding: '5px 12px', fontSize: 10.5, color: 'var(--muted)', borderTop: '1px solid var(--border)' }}>
                ↑↓ to navigate · Enter to pick · Esc to dismiss
              </div>
            </div>
          )}

          <button className="btn-primary" style={{ marginTop: 10 }} onClick={saveNote} disabled={saving || !text.trim()}>
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </div>

        <div className="card">
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <NavIcon name="clipboard" size={14} /> Your Shortcuts
          </div>
          <input
            placeholder="Search…"
            value={refSearch}
            onChange={e => setRefSearch(e.target.value)}
            style={{
              width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 7, padding: '7px 10px', color: 'var(--text)', fontSize: 12.5, marginBottom: 8,
            }}
          />
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {filteredRef.length === 0 ? (
              <p className="text-muted" style={{ fontSize: 12.5 }}>
                {shortcuts.length === 0 ? 'No shortcuts set up yet — add some on the Shortcuts page.' : 'No matches.'}
              </p>
            ) : (
              filteredRef.map(s => (
                <div key={s.id} style={{
                  display: 'flex', justifyContent: 'space-between', gap: 8,
                  padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12.5,
                }}>
                  <span className="mono" style={{ color: 'var(--accent)', fontWeight: 600, flexShrink: 0 }}>.{s.shortcut}</span>
                  <span className="text-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>{s.productName}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="section-title" style={{ marginBottom: 10 }}>
        Pending Review {pending.length > 0 && <span className="badge badge-blue">{pending.length}</span>}
      </div>

      {loading ? (
        <p className="text-muted">Loading…</p>
      ) : pending.length === 0 ? (
        <div className="empty-state"><div className="icon"><NavIcon name="document" size={32} /></div><div>No pending notes — all caught up.</div></div>
      ) : (
        pending.map(note => (
          <NoteCard key={note.id} note={note} products={products} onChanged={load} />
        ))
      )}
    </div>
  );
}
